import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { $generateHtmlFromNodes } from "@lexical/html";
import { LinkNode } from "@lexical/link";
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode } from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { $insertNodeToNearestRoot } from "@lexical/utils";
import {
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  PASTE_COMMAND,
  createCommand,
  FORMAT_TEXT_COMMAND,
  type EditorState,
  type LexicalEditor,
} from "lexical";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatBytes, TICKET_MAX_BYTES, TICKET_MAX_LABEL, ticketPayloadBytes } from "../../utils/ticketPayload";
import { $createImageNode, ImageNode } from "./ImageNode";

const INSERT_IMAGE_COMMAND = createCommand<string>("INSERT_IMAGE_COMMAND");

function insertDataUrl(editor: LexicalEditor, src: string) {
  editor.dispatchCommand(INSERT_IMAGE_COMMAND, src);
}

function readImageFile(editor: LexicalEditor, file: File | undefined) {
  if (!file || !file.type.startsWith("image/")) return;
  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === "string") {
      insertDataUrl(editor, reader.result);
    }
  };
  reader.readAsDataURL(file);
}

function Toolbar() {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unregisterInsert = editor.registerCommand(
      INSERT_IMAGE_COMMAND,
      (src) => {
        editor.update(() => {
          $insertNodeToNearestRoot($createImageNode(src));
        });
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
    const unregisterPaste = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboard = event instanceof ClipboardEvent ? event.clipboardData : null;
        const items = clipboard?.items;
        if (!items) return false;
        for (const item of Array.from(items)) {
          if (item.type.startsWith("image/")) {
            event.preventDefault();
            readImageFile(editor, item.getAsFile() || undefined);
            return true;
          }
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
    return () => {
      unregisterInsert();
      unregisterPaste();
    };
  }, [editor]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.5, bgcolor: "background.default" }}>
      <Tooltip title={t("editor.bold")}>
        <IconButton size="small" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}>
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("editor.italic")}>
        <IconButton size="small" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}>
          <FormatItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("editor.bullet")}>
        <IconButton size="small" onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}>
          <FormatListBulletedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("editor.numbered")}>
        <IconButton size="small" onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}>
          <FormatListNumberedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("editor.image")}>
        <IconButton size="small" onClick={() => fileRef.current?.click()}>
          <ImageOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          readImageFile(editor, e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </Box>
  );
}

function HtmlSyncPlugin({ onHtml }: { onHtml: (html: string) => void }) {
  const [editor] = useLexicalComposerContext();
  return (
    <OnChangePlugin
      onChange={(state: EditorState) => {
        state.read(() => {
          onHtml($generateHtmlFromNodes(editor, null));
        });
      }}
    />
  );
}

export function TicketRichEditor({
  title,
  html,
  onHtml,
  extraBytes = 0,
  placeholder,
}: {
  title: string;
  html: string;
  onHtml: (html: string) => void;
  extraBytes?: number;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const hint = placeholder || t("editor.placeholder");
  const bytes = ticketPayloadBytes(title, html) + extraBytes;
  const over = bytes > TICKET_MAX_BYTES;
  const initialConfig = useMemo(
    () => ({
      namespace: "support-ticket",
      theme: {
        paragraph: "ticket-editor-p",
        list: { ul: "ticket-editor-ul", ol: "ticket-editor-ol", listitem: "ticket-editor-li" },
        text: { bold: "ticket-editor-bold", italic: "ticket-editor-italic" },
      },
      onError(error: Error) {
        console.error(error);
      },
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, ImageNode],
    }),
    []
  );

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: over ? "error.main" : "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
        "& .ticket-editor-p": { margin: "0 0 8px" },
        "& .ticket-editor-bold": { fontWeight: 700 },
        "& .ticket-editor-italic": { fontStyle: "italic" },
        "& .ticket-editor-ul, & .ticket-editor-ol": { margin: "0 0 8px", paddingLeft: 24 },
      }}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <Toolbar />
        <Divider />
        <Box sx={{ position: "relative", minHeight: 220, px: 1.5, py: 1 }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                style={{ outline: "none", minHeight: 200, fontSize: 15, lineHeight: 1.55 }}
                aria-placeholder={hint}
                placeholder={
                  <Typography
                    color="text.secondary"
                    sx={{ position: "absolute", top: 12, left: 16, pointerEvents: "none" }}
                  >
                    {hint}
                  </Typography>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </Box>
        <HistoryPlugin />
        <ListPlugin />
        <HtmlSyncPlugin onHtml={onHtml} />
      </LexicalComposer>
      <Divider />
      <Typography variant="caption" color={over ? "error" : "text.secondary"} sx={{ display: "block", px: 1.5, py: 0.75 }}>
        {over ? t("tickets.tooLarge") : t("tickets.payloadSize", { used: formatBytes(bytes), max: TICKET_MAX_LABEL })}
      </Typography>
    </Box>
  );
}
